import { useState } from "react";
import {
  ArrowLeft,
  FileText,
  History,
  Search,
  WalletCards,
  Download,
} from "lucide-react";

import Loading from "../common/LoaderCommon";
import PaginationComponent from "../PaginationComponent";
import VendorPaymentHistoryModal from "./VendorPaymentHistoryModal";
import { Vendor, VendorInvoice } from "../../types/vendorManagement";
import { Input } from "../../utils/components/ui/input";
import { cn } from "../../utils/cn";

type VendorManagementDetailProps = {
  vendor: Vendor;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  isPurchaseHistoryLoading: boolean;
  isPurchaseHistoryError: boolean;
  purchaseCurrentPage: number;
  purchaseTotalPages: number;
  purchaseTotalCount: number;
  onPurchasePageChange: (page: number) => void;
  startDate?: string;
  onStartDateChange?: (value: string) => void;
  endDate?: string;
  onEndDateChange?: (value: string) => void;
  onBack: () => void;
  onPayInvoice: (invoice: VendorInvoice) => void;
  onViewInvoiceDetails: (invoice: VendorInvoice) => void;
  onEditVendor: () => void;
  onDeleteVendor: () => void;
  onWriteOffInvoice?: (invoice: VendorInvoice) => void;
  onExportExcel?: () => void;
  isExportingExcel?: boolean;
};

const formatCurrency = (amount: number) =>
  `OMR ${new Intl.NumberFormat("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(amount)}`;

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="mb-2 text-sm text-[#8A948D]">{label}</p>
      <div className="rounded-[12px] bg-[#F4F7FC] px-4 py-3 text-[15px] font-semibold text-[#354438]">
        {value}
      </div>
    </div>
  );
}

function VendorManagementDetail({
  vendor,
  searchTerm,
  onSearchChange,
  isPurchaseHistoryLoading,
  isPurchaseHistoryError,
  purchaseCurrentPage,
  purchaseTotalPages,
  purchaseTotalCount,
  onPurchasePageChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  onBack,
  onPayInvoice,
  onViewInvoiceDetails,
  onEditVendor,
  onDeleteVendor,
  onWriteOffInvoice,
  onExportExcel,
  isExportingExcel,
}: VendorManagementDetailProps) {
  const [isPaymentHistoryOpen, setIsPaymentHistoryOpen] = useState(false);
  const invoices = vendor.invoices;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 border-b border-[#E8EEF9] pb-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1">
          <button
            type="button"
            onClick={onBack}
            className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-[#7A877F] transition-colors hover:text-[#526054]"
          >
            <ArrowLeft className="size-4" />
            Back to vendor list
          </button>
          <h1 className="text-[34px] font-semibold tracking-[-0.02em] text-[#1A5EF9]">
            Vendor Detail
          </h1>
          <p className="mt-2 text-sm text-[#7A877F]">
            Vendor |{" "}
            <span className="font-semibold text-[#38463A]">
              {vendor.vendorName}
            </span>
          </p>
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={onEditVendor}
              className="inline-flex items-center gap-2 rounded-lg border border-[#DCE7FF] bg-white px-4 py-2 text-sm font-semibold text-[#1A5EF9] transition hover:bg-[#F6F9FF]"
            >
              Edit Vendor
            </button>
            <button
              onClick={onDeleteVendor}
              className="inline-flex items-center gap-2 rounded-lg border border-[#FFDCDC] bg-white px-4 py-2 text-sm font-semibold text-[#D93A3A] transition hover:bg-[#FFF4F4]"
            >
              Delete Vendor
            </button>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="rounded-[18px] border border-[#DCE7FF] bg-[#F6F9FF] px-5 py-4 text-sm text-[#647164] w-full min-w-[200px]">
            <p className="font-semibold text-[#415043]">Outstanding Summary</p>
            <p className="mt-1">
              Overdue {formatCurrency(vendor.overdueAmount)}
            </p>
          </div>
          <button
            onClick={() => setIsPaymentHistoryOpen(true)}
            className="inline-flex w-full min-w-[200px] items-center justify-center gap-2 rounded-[18px] border border-[#DCE7FF] bg-white px-5 py-3 text-sm font-semibold text-[#1A5EF9] transition hover:bg-[#F6F9FF]"
          >
            <History className="size-4" />
            Payment History
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_42px_rgba(26,94,249,0.08)]">
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          <DetailField label="Vendor Name" value={vendor.vendorName} />
          <DetailField label="Contact Person" value={vendor.contactPerson} />
          <DetailField label="VATIN" value={vendor.vatin || "-"} />
          <DetailField label="Phone Number" value={vendor.phoneNumber} />
          <DetailField label="Email Address" value={vendor.emailAddress} />
          <DetailField
            label="Credit Period"
            value={`${vendor.creditPeriod} days`}
          />
          <DetailField
            label="Credit Limit"
            value={formatCurrency(vendor.creditLimit)}
          />
          <DetailField
            label="Outstanding"
            value={formatCurrency(vendor.outstanding)}
          />
          <DetailField
            label="Overdue Amount"
            value={formatCurrency(vendor.overdueAmount)}
          />
          <DetailField
            label="Available Credit"
            value={formatCurrency(vendor.availableCredit)}
          />
          <DetailField
            label="Overdue Days"
            value={`${vendor.overdueDays} days`}
          />
        </div>
      </div>

      <div className="rounded-3xl border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_42px_rgba(26,94,249,0.08)]">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-[24px] font-semibold text-[#1A5EF9]">
              Purchase History
            </h2>
            <p className="mt-1 text-sm text-[#7A877F]">
              Review open balances and settle invoices directly from this view.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={onExportExcel}
                disabled={isExportingExcel}
                className="h-11 flex items-center gap-2 rounded-[12px] border border-[#1A5EF9] px-4 text-sm font-semibold text-[#1A5EF9] transition hover:bg-[#EEF4FF] disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export Excel"
              >
                <Download className={`size-4 ${isExportingExcel ? "animate-bounce" : ""}`} />
                <span className="hidden sm:inline">
                  {isExportingExcel ? "Exporting..." : "Export Excel"}
                </span>
              </button>
              <Input
                type="date"
                value={startDate || ""}
                onChange={(e) => onStartDateChange?.(e.target.value)}
                className="h-11 rounded-[12px] border-[#E6EDF8] bg-[#F4F7FC] text-sm shadow-none focus-visible:ring-[#C9D8FF]"
              />
              <span className="text-sm text-slate-500">to</span>
              <Input
                type="date"
                value={endDate || ""}
                onChange={(e) => onEndDateChange?.(e.target.value)}
                className="h-11 rounded-[12px] border-[#E6EDF8] bg-[#F4F7FC] text-sm shadow-none focus-visible:ring-[#C9D8FF]"
              />
            </div>
            <div className="relative w-full max-w-[320px] sm:w-[240px]">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#9BA7A0]" />
              <Input
                value={searchTerm}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Search invoice"
                className="h-11 rounded-[12px] border-[#E6EDF8] bg-[#F4F7FC] pl-11 text-sm shadow-none focus-visible:ring-[#C9D8FF]"
              />
            </div>
          </div>
        </div>

        {isPurchaseHistoryLoading ? (
          <div className="py-12">
            <Loading />
          </div>
        ) : isPurchaseHistoryError ? (
          <div className="rounded-[18px] border border-[#F2D9D9] bg-[#FFF8F8] px-4 py-10 text-center text-sm text-[#B15454]">
            Unable to load purchase history right now.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-[#F4F7FC] text-left text-sm font-semibold text-[#344338]">
                  <th className="px-4 py-4">Invoice ID</th>
                  <th className="px-4 py-4">Invoice Date</th>
                  <th className="px-4 py-4">Amount</th>
                  <th className="px-4 py-4">Payment Date</th>
                  <th className="px-4 py-4">Amount Paid</th>
                  <th className="px-4 py-4">Balance Amount</th>
                  <th className="px-4 py-4">Due Date</th>
                  <th className="px-4 py-4">Overdue Days</th>
                  <th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className="border-t border-[#EEF2F9] text-sm text-[#405144]"
                  >
                    <td className="px-4 py-5 font-semibold text-[#2D3B30]">
                      {invoice.invoiceId}
                    </td>
                    <td className="px-4 py-5">{invoice.invoiceDate}</td>
                    <td className="px-4 py-5">
                      {formatCurrency(invoice.amount)}
                    </td>
                    <td className="px-4 py-5">{invoice.paymentDate}</td>
                    <td className="px-4 py-5">
                      {formatCurrency(invoice.amountPaid)}
                    </td>
                    <td className="px-4 py-5">
                      {formatCurrency(invoice.balanceAmount)}
                    </td>
                    <td className="px-4 py-5">{invoice.dueDate}</td>
                    <td
                      className={cn(
                        "px-4 py-5 font-medium",
                        invoice.overdueDays > 0
                          ? "text-[#EB5F67]"
                          : "text-[#405144]",
                      )}
                    >
                      {invoice.overdueDays > 0
                        ? `${invoice.overdueDays} Days`
                        : "0 Days"}
                    </td>
                    <td
                      className={cn(
                        "px-4 py-5 font-semibold",
                        invoice.status === "Overdue" && "text-[#EB5F67]",
                        invoice.status === "Paid" && "text-[#55A06A]",
                        invoice.status === "Opening Balance" &&
                          "text-[#5577E6]",
                      )}
                    >
                      {invoice.status}
                    </td>
                    <td className="px-4 py-5">
                      <div className="flex items-center justify-center gap-3">
                        <button
                          type="button"
                          className="text-[#68766A] transition-colors hover:text-[#3E4E41]"
                          title={`View ${invoice.invoiceId}`}
                          aria-label={`View ${invoice.invoiceId}`}
                          onClick={() => onViewInvoiceDetails(invoice)}
                        >
                          <FileText className="size-4" />
                        </button>
                        {invoice.balanceAmount > 0 && (
                          <button
                            type="button"
                            className="text-[#68766A] transition-colors hover:text-[#1A5EF9]"
                            title={`Pay ${invoice.invoiceId}`}
                            aria-label={`Pay ${invoice.invoiceId}`}
                            onClick={() => onPayInvoice(invoice)}
                          >
                            <WalletCards className="size-4" />
                          </button>
                        )}
                        {invoice.balanceAmount > 0 && localStorage.getItem("role") === "ADMIN" && onWriteOffInvoice && (
                          <button
                            type="button"
                            className="rounded-lg border border-[#BFD1FF] px-2 py-1 text-xs font-semibold text-[#1A5EF9] transition hover:bg-[#EEF4FF] hover:text-[#0E4FDE]"
                            onClick={() => onWriteOffInvoice(invoice)}
                          >
                            Write Off
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isPurchaseHistoryLoading &&
          !isPurchaseHistoryError &&
          invoices.length === 0 && (
            <div className="rounded-[18px] border border-dashed border-[#D7E2FB] bg-[#F8FBFF] px-4 py-10 text-center text-sm text-[#8A948D]">
              No invoices match the current search.
            </div>
          )}

        <div className="mt-6 flex flex-col gap-4 rounded-[18px] bg-[#F6F9FF] px-4 py-3 text-sm text-[#8A948D] lg:flex-row lg:items-center lg:justify-between">
          <p>
            {invoices.length} of {purchaseTotalCount} invoice entries visible
          </p>
          {purchaseTotalPages > 1 && (
            <PaginationComponent
              currentPage={purchaseCurrentPage}
              totalPages={purchaseTotalPages}
              onPageChange={onPurchasePageChange}
            />
          )}
        </div>
      </div>

      {isPaymentHistoryOpen && (
        <VendorPaymentHistoryModal
          vendorId={vendor.id}
          open={isPaymentHistoryOpen}
          setOpen={setIsPaymentHistoryOpen}
        />
      )}
    </div>
  );
}

export default VendorManagementDetail;
